import React, { useContext, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Clear from '@mui/icons-material/Clear';
import TopBar from '../components/common/TopBar';
import { btnSubtleNeutralSx, filterInputSx } from '../components/common/filterStyles';
import RulesetList from '../components/rulesets/RulesetList';
import RulesetBuilderDrawer from '../components/rulesets/RulesetBuilderDrawer';
import RulesetDelete from '../components/rulesets/RulesetDelete';
import { useRulesets } from '../components/rulesets/hooks';
import { useSetTitle } from '../components/hooks/useSetTitle';
import useSetOrg from '../components/hooks/useSetOrg';
import { useDebouncedSearchParam } from '../components/hooks/useDebouncedSearchParam';
import { IRuleset, RulesetBuilderMode } from '../types/ruleset-types';
import { AppContext } from '../App';
import { DEFAULT_LIMIT } from '../constants';
import TableStyles from '../components/documents/Table.module.css';

const RulesetsListPage: React.FC = () => {
  const { dpid } = useParams<{ dpid: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { setToast } = useContext(AppContext);

  useSetTitle('Rulesets');
  useSetOrg(dpid);

  const [selectedRuleset, setSelectedRuleset] = React.useState<IRuleset | null>(null);
  const [rulesetToDelete, setRulesetToDelete] = React.useState<IRuleset | null>(null);

  // Read URL params
  const searchParams = new URLSearchParams(location.search);
  const editId = searchParams.get('edit');
  const copyId = searchParams.get('copy');
  const isCreating = searchParams.get('create') === 'true';
  const builderMode = copyId
    ? RulesetBuilderMode.Copy
    : editId
      ? RulesetBuilderMode.Edit
      : RulesetBuilderMode.Create;
  const sourceId = builderMode === RulesetBuilderMode.Copy ? copyId : editId;
  const perPage = Number(searchParams.get('perPage')) || DEFAULT_LIMIT;
  const page = Number(searchParams.get('page')) || 0;
  const labelContains = searchParams.get('ilabel__contains') || '';

  const { data, isLoading, isError, refetch } = useRulesets(
    dpid || '',
    {
      ordering: 'priority',
      limit: perPage,
      offset: page * perPage,
      ...(labelContains ? { ilabel__contains: labelContains } : {}),
    },
    !!dpid
  );
  const count = data?.count || 0;

  const {
    localValue: localLabel,
    setLocalValue: setLocalLabel,
    debouncedSet: debouncedSetLabel,
    clear: clearLabel,
  } = useDebouncedSearchParam('ilabel__contains', labelContains);

  const updateUrl = (params: { edit?: string; copy?: string; create?: boolean } | null) => {
    const newParams = new URLSearchParams(location.search);
    newParams.delete('edit');
    newParams.delete('copy');
    newParams.delete('create');

    if (params?.edit) {
      newParams.set('edit', params.edit);
    } else if (params?.copy) {
      newParams.set('copy', params.copy);
    } else if (params?.create) {
      newParams.set('create', 'true');
    }

    navigate({ search: newParams.toString() }, { replace: true });
  };

  // Resolve the URL's source ruleset from the loaded page. An id that is not
  // on this page (stale deep link, deleted ruleset) is dropped from the URL
  // rather than opening the builder without a source.
  useEffect(() => {
    if (!sourceId) {
      setSelectedRuleset(null);
      return;
    }
    if (!data?.results) {
      return;
    }
    const ruleset = data.results.find((r) => r.id === sourceId);
    if (ruleset) {
      setSelectedRuleset(ruleset);
    } else {
      updateUrl(null);
    }
    // updateUrl is recreated every render; the effect only needs to re-run on
    // the resolved id or a fresh page of results.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId, data?.results]);

  // The drawer waits for a source in Edit/Copy mode so it never opens half-seeded.
  const drawerOpen = isCreating || (!!sourceId && !!selectedRuleset);

  const handleCreateNew = () => {
    setSelectedRuleset(null);
    updateUrl({ create: true });
  };

  const handleEdit = (ruleset: IRuleset) => {
    setSelectedRuleset(ruleset);
    updateUrl({ edit: ruleset.id });
  };

  const handleCopy = (ruleset: IRuleset) => {
    setSelectedRuleset(ruleset);
    updateUrl({ copy: ruleset.id });
  };

  const handleDelete = (ruleset: IRuleset) => {
    setRulesetToDelete(ruleset);
  };

  const handleDrawerClose = () => {
    setSelectedRuleset(null);
    updateUrl(null);
  };

  const handleDrawerSuccess = () => {
    setSelectedRuleset(null);
    updateUrl(null);
    refetch();
  };

  const handleDeleteClose = () => {
    setRulesetToDelete(null);
  };

  const handleDeleteSuccess = () => {
    setRulesetToDelete(null);
    setToast({
      variant: 'success',
      open: true,
      message: 'Ruleset deleted successfully',
    });
  };

  return (
    <div>
      <TopBar />
      <div className={TableStyles.documentsPageWrapper}>
        {/* Row 1: Title bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Rulesets
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: '#65BBA9', fontWeight: 600 }}
            >
              {isLoading ? '...' : (
                <>(<span data-cy="span-rulesets-count">{count.toLocaleString()}</span>)</>
              )}
            </Typography>
          </Box>
          <Button
            startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
            onClick={handleCreateNew}
            data-cy="btn-create-ruleset"
            sx={{
              ...btnSubtleNeutralSx,
              borderColor: '#1D9AAA',
              color: '#1D9AAA',
            }}
          >
            Create
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1.5,
            justifyContent: 'flex-end',
          }}
        >
          <TextField
            size="small"
            placeholder="Search by label..."
            variant="outlined"
            autoComplete="off"
            value={localLabel}
            onChange={(e) => {
              setLocalLabel(e.target.value);
              debouncedSetLabel(e.target.value);
            }}
            data-cy="text-ruleset-search-input"
            sx={{ width: 220, ...filterInputSx }}
            InputProps={{
              endAdornment: localLabel && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => clearLabel()}
                    edge="end"
                    data-cy="btn-ruleset-search-clear"
                    sx={{ mr: -1, p: 0.25 }}
                  >
                    <Clear sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {isLoading ? (
          <LinearProgress />
        ) : isError ? (
          <Typography color="error">Failed to load rulesets</Typography>
        ) : (
          <RulesetList
            rulesets={data?.results || []}
            dataPoolId={dpid || ''}
            count={count}
            page={page}
            perPage={perPage}
            onEdit={handleEdit}
            onCopy={handleCopy}
            onDelete={handleDelete}
            onRefresh={refetch}
          />
        )}

        <RulesetBuilderDrawer
          open={drawerOpen}
          onClose={handleDrawerClose}
          onSuccess={handleDrawerSuccess}
          dataPoolId={dpid || ''}
          mode={builderMode}
          ruleset={selectedRuleset}
          existingLabels={(data?.results ?? []).map((r) => r.label)}
        />

        {rulesetToDelete && (
          <RulesetDelete
            dataPoolId={dpid || ''}
            ruleset={rulesetToDelete}
            open
            onClose={handleDeleteClose}
            onSuccess={handleDeleteSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default RulesetsListPage;
